import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { useAuth } from '../context/AuthContext';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import { SkeletonTable } from '../components/common/Skeleton';
import { ArrowUpRight, Wallet, DollarSign, Send, Hash, ShieldAlert, CheckCircle2, AlertCircle } from 'lucide-react';

const WithdrawalPage = () => {
  const { refreshUser } = useAuth();
  const [walletBalance, setWalletBalance] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Form states
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('USDT-TRC20');
  const [walletAddress, setWalletAddress] = useState('');

  const fetchWithdrawalData = async () => {
    try {
      const resWallet = await axiosClient.get('/wallet/my-wallet');
      setWalletBalance(resWallet.data.wallet?.balance || 0);

      const resWithdraw = await axiosClient.get('/withdrawal/my-requests');
      setWithdrawals(resWithdraw.data.withdrawals || []);
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('[Withdrawal Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawalData();
  }, []);

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !walletAddress) return;
    if (Number(amount) > walletBalance) {
      setFeedbackMsg({ type: 'error', text: `Insufficient balance. Available: $${walletBalance.toFixed(2)}` });
      return;
    }

    setSubmitting(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      const res = await axiosClient.post('/withdrawal/request', {
        amount,
        network,
        walletAddress
      });
      setFeedbackMsg({ type: 'success', text: 'Withdrawal request submitted successfully! Admin will process crypto payout.' });
      setAmount('');
      setWalletAddress('');
      setShowWithdrawModal(false);
      fetchWithdrawalData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit withdrawal request.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <h2 className="text-lg font-extrabold text-[#FFFFFF]">Crypto Withdrawal Portal</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Request USDT withdrawals directly to your destination crypto wallet address. Admin processes payout and records blockchain TXID.
          </p>
        </div>

        <div className="flex items-center space-x-3 shrink-0">
          <div className="bg-[#09090B] px-3.5 py-2 rounded-xl border border-[#27272A] flex items-center space-x-2">
            <Wallet className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs text-[#9CA3AF]">Available:</span>
            <span className="text-xs font-bold text-[#10B981]">${walletBalance.toFixed(2)}</span>
          </div>

          <Button 
            variant="primary" 
            onClick={() => setShowWithdrawModal(true)}
            disabled={walletBalance < 10}
            className="flex items-center space-x-2"
          >
            <ArrowUpRight className="w-4 h-4" />
            <span>Request Withdrawal</span>
          </Button>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Withdrawal Requests History */}
      <Card title="Your Crypto Withdrawal Requests" subtitle="Real-time payout processing status & recorded blockchain TXID hash">
        {loading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : withdrawals.length === 0 ? (
          <div className="p-8 text-center space-y-3">
            <ArrowUpRight className="w-10 h-10 text-[#9CA3AF]/40 mx-auto" />
            <p className="text-xs text-[#9CA3AF]">No withdrawal requests submitted yet.</p>
            {walletBalance >= 10 && (
              <Button variant="secondary" size="sm" onClick={() => setShowWithdrawModal(true)}>
                Submit First Withdrawal Request
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Network</th>
                  <th className="py-3 px-4">Destination Wallet Address</th>
                  <th className="py-3 px-4">Payout Status</th>
                  <th className="py-3 px-4">Payout TXID</th>
                  <th className="py-3 px-4">Submitted Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {withdrawals.map((w) => (
                  <tr key={w._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-bold text-[#EF4444]">
                      -${w.amount.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">
                      {w.network}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#FFFFFF]">
                      {w.walletAddress.substring(0, 14)}...
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={w.status} label={w.status} />
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#F59E0B]">
                      {w.txid ? `${w.txid.substring(0, 12)}...` : '-'}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(w.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Withdrawal Request Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF]">Request Crypto Withdrawal</h3>
              <button onClick={() => setShowWithdrawModal(false)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A] flex justify-between items-center text-xs">
              <span className="text-[#9CA3AF]">Available Balance:</span>
              <span className="font-bold text-[#10B981]">${walletBalance.toFixed(2)} USDT</span>
            </div>

            <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Network</label>
                <select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] rounded-xl px-3 py-3 text-xs focus:outline-none focus:border-[#F97316]"
                >
                  <option value="USDT-TRC20">USDT (TRC20 - Tron Network)</option>
                  <option value="USDT-BEP20">USDT (BEP20 - BNB Smart Chain)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Withdrawal Amount ($ USD)</label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="number"
                    min="10"
                    max={walletBalance}
                    required
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Min $10"
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-bold focus:outline-none focus:border-[#F97316]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Destination Crypto Wallet Address</label>
                <div className="relative">
                  <Send className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    required
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Paste destination wallet address..."
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                  />
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-1">
                  ⚠️ Verify destination address carefully. Amount is locked upon request submission.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowWithdrawModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalPage;
